import React, { useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useBlock } from "@ivanholiak/easy-email-editor";
import type { BrenvioCondition } from "./easyEmailHelpers";

/**
 * Painel nativo do Easy Email ao selecionar um WRAPPER condicional (LAC-131).
 * Por quê: editar a regra sem abrir o modal “Editar condição”.
 */
const ConditionalBlockAttributePanel: React.FC = () => {
  const { t } = useTranslation();
  const { focusBlock, setFocusBlock } = useBlock();
  const existing = focusBlock?.data?.value?.brenvioCondition as
    | BrenvioCondition
    | undefined;

  const [type, setType] = useState<"tag" | "field" | "segment">(
    existing?.type === "field" || existing?.type === "segment"
      ? existing.type
      : "tag"
  );
  const [tagValues, setTagValues] = useState(
    existing?.type === "tag" ? existing.values.join(", ") : "vip"
  );
  const [fieldSlug, setFieldSlug] = useState(
    existing?.type === "field" ? existing.slug : ""
  );
  const [fieldValue, setFieldValue] = useState(
    existing?.type === "field" ? existing.value : ""
  );
  const [segmentId, setSegmentId] = useState(
    existing?.type === "segment" ? String(existing.id) : ""
  );

  const existingKey = JSON.stringify(existing ?? null);

  useEffect(() => {
    setType(
      existing?.type === "field" || existing?.type === "segment"
        ? existing.type
        : "tag"
    );
    setTagValues(existing?.type === "tag" ? existing.values.join(", ") : "vip");
    setFieldSlug(existing?.type === "field" ? existing.slug : "");
    setFieldValue(existing?.type === "field" ? existing.value : "");
    setSegmentId(existing?.type === "segment" ? String(existing.id) : "");
  }, [existingKey]);

  const canSave = useMemo(() => {
    if (type === "tag") {
      return tagValues.split(",").map((s) => s.trim()).some(Boolean);
    }
    if (type === "field") {
      return fieldSlug.trim() !== "";
    }
    return Number(segmentId) > 0;
  }, [type, tagValues, fieldSlug, segmentId]);

  if (!existing) {
    return null;
  }

  const save = () => {
    if (!focusBlock || !canSave) {
      return;
    }
    let condition: BrenvioCondition;
    if (type === "tag") {
      condition = {
        type: "tag",
        op: "in",
        values: tagValues
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
    } else if (type === "field") {
      condition = {
        type: "field",
        slug: fieldSlug.trim(),
        op: "eq",
        value: fieldValue.trim(),
      };
    } else {
      condition = { type: "segment", id: Number(segmentId) };
    }
    const next = {
      ...focusBlock,
      data: {
        ...focusBlock.data,
        value: {
          ...focusBlock.data.value,
          brenvioCondition: condition,
        },
      },
    };
    setFocusBlock(next);
  };

  return (
    <div className="px-2 py-2 border-bottom">
      <div className="fw-medium mb-2">
            {t("campaigns.form.editor.conditionalEditTitle")}
          </div>
          <FormGroup>
            <Label className="form-label">{t("campaigns.form.editor.conditionalType")}</Label>
            <Input
              type="select"
              value={type}
              onChange={(e) =>
                setType(e.target.value as "tag" | "field" | "segment")
              }
            >
              <option value="tag">{t("campaigns.form.editor.conditionalTypeTag")}</option>
              <option value="field">{t("campaigns.form.editor.conditionalTypeField")}</option>
              <option value="segment">
                {t("campaigns.form.editor.conditionalTypeSegment")}
              </option>
            </Input>
          </FormGroup>
          {type === "tag" ? (
            <FormGroup>
              <Label className="form-label">{t("campaigns.form.editor.conditionalTags")}</Label>
              <Input
                value={tagValues}
                onChange={(e) => setTagValues(e.target.value)}
              />
            </FormGroup>
          ) : null}
          {type === "field" ? (
            <>
              <FormGroup>
                <Label className="form-label">{t("campaigns.form.editor.conditionalFieldSlug")}</Label>
                <Input
                  value={fieldSlug}
                  onChange={(e) => setFieldSlug(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label className="form-label">{t("campaigns.form.editor.conditionalFieldValue")}</Label>
                <Input
                  value={fieldValue}
                  onChange={(e) => setFieldValue(e.target.value)}
                />
              </FormGroup>
            </>
          ) : null}
          {type === "segment" ? (
            <FormGroup>
              <Label className="form-label">{t("campaigns.form.editor.conditionalSegmentId")}</Label>
              <Input
                type="number"
                value={segmentId}
                onChange={(e) => setSegmentId(e.target.value)}
              />
            </FormGroup>
          ) : null}
          <Button color="primary" size="sm" disabled={!canSave} onClick={save}>
            {t("campaigns.form.editor.conditionalUpdate")}
          </Button>
    </div>
  );
};

export default ConditionalBlockAttributePanel;
